// Copyright 2026 FlairCV Contributors
// SPDX-License-Identifier: AGPL-3.0-or-later

import { cn } from '@/lib/utils';

// ── Page Header ─────────────────────────────────────────────
// Stacks title above actions on mobile, side-by-side on sm+.

interface PageHeaderProps {
  title: string;
  description?: string;
  children?: React.ReactNode; // action buttons
}

export function PageHeader({ title, description, children }: PageHeaderProps) {
  return (
    <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
      <div>
        <h1 className="text-[22px] font-semibold tracking-tight text-foreground sm:text-[26px]">
          {title}
        </h1>
        {description && (
          <p className="mt-1 text-[14px] text-muted-foreground sm:text-[15px]">
            {description}
          </p>
        )}
      </div>
      {children && (
        <div className="flex items-center gap-2">{children}</div>
      )}
    </div>
  );
}

// ── Entry Card ──────────────────────────────────────────────
// Card with optional selection checkbox and action buttons.
// Desktop: content and actions side-by-side.
// Mobile: actions below content.

interface EntryCardProps {
  selected?: boolean;
  className?: string;
  children: React.ReactNode;
  actions?: React.ReactNode;
}

export function EntryCard({
  selected,
  className,
  children,
  actions,
}: EntryCardProps) {
  return (
    <div
      className={cn(
        'group rounded-lg border bg-card transition-colors hover:border-border/80',
        selected
          ? 'border-primary/40 bg-primary/[0.02]'
          : 'border-border',
        className,
      )}
    >
      <div className="p-3 sm:p-4">
        <div className="sm:flex sm:items-start sm:justify-between sm:gap-3">
          <div className="min-w-0 flex-1">{children}</div>
          {actions && (
            <div className="hidden shrink-0 items-center gap-1 sm:flex">
              {actions}
            </div>
          )}
        </div>
        {actions && (
          <div className="mt-2 flex items-center gap-1 sm:hidden">
            {actions}
          </div>
        )}
      </div>
    </div>
  );
}

// ── Form Card ───────────────────────────────────────────────
// Responsive padding wrapper for create/edit forms.

interface FormCardProps {
  children: React.ReactNode;
  className?: string;
}

export function FormCard({ children, className }: FormCardProps) {
  return (
    <div
      className={cn(
        'mt-4 rounded-lg border border-border bg-card p-4 sm:mt-6 sm:p-6',
        className,
      )}
    >
      {children}
    </div>
  );
}

// ── Field Header ────────────────────────────────────────────
// Label + optional action (e.g. AI Polish button). Stacks on mobile.

interface FieldHeaderProps {
  htmlFor?: string;
  label: string;
  hint?: string; // e.g. "(optional)"
  children?: React.ReactNode; // right-side actions
}

export function FieldHeader({
  htmlFor,
  label,
  hint,
  children,
}: FieldHeaderProps) {
  return (
    <div className="mb-1.5 flex flex-col gap-1.5 sm:flex-row sm:items-center sm:justify-between">
      <label
        htmlFor={htmlFor}
        className="block text-[14px] font-medium text-foreground"
      >
        {label}
        {hint && (
          <>
            {' '}
            <span className="font-normal text-muted-foreground">{hint}</span>
          </>
        )}
      </label>
      {children && (
        <div className="flex items-center gap-3">{children}</div>
      )}
    </div>
  );
}

// ── Detail Line ─────────────────────────────────────────────
// Bullet-point item with text + action buttons.
// Mobile: actions below text (full width, always visible).
// Desktop: actions right-aligned on same row, hover-reveal.

interface DetailLineProps {
  children: React.ReactNode; // text content
  actions?: React.ReactNode; // action buttons
  polished?: React.ReactNode; // polished/AI version below text
  suggestion?: React.ReactNode; // inline AI suggestion panel
}

export function DetailLine({
  children,
  actions,
  polished,
  suggestion,
}: DetailLineProps) {
  return (
    <li className="rounded-md px-2 py-1.5 transition-colors hover:bg-muted/50">
      <div className="flex items-start gap-2">
        <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-muted-foreground/40" />
        <span className="min-w-0 flex-1 text-[14px] leading-relaxed text-foreground">
          {children}
        </span>
        {/* Desktop: inline right-aligned */}
        {actions && (
          <div className="hidden shrink-0 items-center gap-0.5 sm:flex">
            {actions}
          </div>
        )}
      </div>
      {polished && <div className="mt-1 ml-3.5">{polished}</div>}
      {/* Mobile: actions below text */}
      {actions && (
        <div className="mt-1 ml-3.5 flex items-center gap-0.5 sm:hidden">
          {actions}
        </div>
      )}
      {suggestion && <div className="mt-1.5 ml-3.5">{suggestion}</div>}
    </li>
  );
}
